import React, { lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router'
import { useFeatureFlag } from './utils/hooks/useFeatureFlag'
import Layout from './components/layouts/Layout'
import LazyLoader from './rsmCoreComponents/LazyLoader'
import ProtectedRoute from './components/ProtectedRoute'
import ProtectedRouteError from './components/ProtectedRouteError'
import Home from './pages/Home'
import Engagement from './pages/Engagement'
import EngagementList from './pages/EngagementList'
import Docs from './pages/Docs'
import Insight from './pages/Insight'
import Insights from './pages/Insights'
import Notifications from './pages/Notifications'
import Profile from './pages/Profile'
import ProjectsPage from './pages/ProjectsPage'
import ReportAnIssue from './pages/ReportAnIssue'
import Faq from './pages/Faq'
import FaqRedesign from './pages/Faq/FaqRedesign'
import FeaturesPage from './pages/FeaturesPage'
import Support from './pages/Support'
import SupportPage from './pages/Support/SupportPage'
import FeedbackForm from './pages/Feedback'
import AdminSettingsForm from './pages/AdminSettings'
import ChildReportPage from './pages/PowerBI/ChildReportPage'
import UserManagement from './pages/UserManagement'
import UserProfile from './components/UserManagement/UserProfile'
import NoMatch from './pages/NoMatch'
import ContactUsBaseLayout from './pages/Support/ContactUsBaseLayout'
import PaymentHistoryLayout from './components/Invoicing/Invoices/PaymentHistoryLayout'
import InvoiceProtectedLayout from './pages/Invoicing'
import InvoicePaymentSuccess from './components/Invoicing/Invoices/InvoicePaymentSuccess'
import InvoicesPaymentTable from './components/Invoicing/Invoices/InvoicesPaymentTable'
import {
  AccountManagement,
  DashboardLayout,
  InvoicesLayout,
  PaymentType,
} from './components/Invoicing'
import {
  CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT,
  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
  CEM_FEATURE_PAYMENT_PAY_INVOICES,
  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
  CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
  CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
  INVOICING_FEATURE_AUTOPAY,
  CEM_FEATURE_MANAGE_ADMIN_SETTINGS,
  CEM_FEATURE_CLIENT_ADMIN,
} from './utils/constants/constants'
import Report from './pages/PowerBI/Report'
import MobileReport from './pages/PowerBI/MobileReport'
import UserManagementLayout from './components/UserManagement/UserManagementLayout'

const DocumentRequestsPage = LazyLoader(
  lazy(() => import('./pages/DocumentRequestsPage')),
)

const QuestionnaireBaseLayout = LazyLoader(
  lazy(() => import('./pages/Questionnaire/QuestionnaireBaseLayout')),
)

const MyQuestionnaireLayout = LazyLoader(
  lazy(() => import('./components/Questionnaire/My/MyQuestionnairesLayout')),
)

const SearchAndAssignLayout = LazyLoader(
  lazy(
    () =>
      import('./components/Questionnaire/SearchAndAssign/SearchAndAssignLayout'),
  ),
)

export const AppRoutes = () => {
  const isSupportEnabled = useFeatureFlag('Support')
  const isUserProfileEnabled = useFeatureFlag('UserProfile')
  const isFAQRedesignEnabled = useFeatureFlag('FAQRedesign')
  const isQuestionnairesEnabled = useFeatureFlag('Questionnaires')
  const isAdminSettingsEnabled = useFeatureFlag('AdminSettings')

  const supportProtectedRoute = (
    children: React.ReactNode,
  ): React.ReactNode => (
    <ProtectedRoute
      feature="Support"
      featureFallback={<Navigate to="/support" replace />}
      matchType="all">
      {children}
    </ProtectedRoute>
  )

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="/" element={<Home />} />
        <Route
          path="/engagements"
          element={
            <ProtectedRoute feature="Targeting_Engagement" matchType="any">
              <EngagementList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagements/:engagementId"
          element={
            <ProtectedRoute feature="Targeting_Engagement" matchType="any">
              <Engagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagements/:engagementId/requests/*"
          element={
            <ProtectedRoute
              feature={['DocumentRequest', 'Targeting_Engagement']}
              matchType="any">
              <DocumentRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagements/:engagementId/docs"
          element={
            <ProtectedRoute
              feature={['DocumentRequest', 'Targeting_Engagement']}
              requireEmployee
              matchType="any">
              <Docs />
            </ProtectedRoute>
          }
        />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:insightId" element={<Insight />} />
        <Route
          path="/invoicing"
          element={
            <ProtectedRoute
              feature="Invoicing"
              cemFeature={[
                CEM_FEATURE_PAYMENT_PAY_INVOICES,
                CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
              ]}
              matchType="any">
              <InvoiceProtectedLayout />
            </ProtectedRoute>
          }>
          <Route
            index
            element={
              <ProtectedRoute
                cemFeature={CEM_FEATURE_PAYMENT_CLIENT_ADMIN}
                cemFeatureFallback={
                  <Navigate to="/invoicing/invoices" replace />
                }
                matchType="any">
                <Navigate to="/invoicing/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute
                cemFeature={CEM_FEATURE_PAYMENT_CLIENT_ADMIN}
                matchType="any">
                <DashboardLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoicing/dashboard/:clientId"
            element={
              <ProtectedRoute
                cemFeature={CEM_FEATURE_PAYMENT_CLIENT_ADMIN}
                matchType="any">
                <DashboardLayout />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoicing/invoices/:clientId"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
                ]}
                matchType="any">
                <InvoicesLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="invoices"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <InvoicesLayout />
              </ProtectedRoute>
            }>
            <Route path="payment-type" element={<PaymentType />} />
            <Route
              path="pay-on-account"
              element={<PaymentType isPayOnAccount />}
            />
            {/* add protected route for autopay feature flag */}
            <Route
              path="autopay"
              element={
                <ProtectedRoute
                  feature={INVOICING_FEATURE_AUTOPAY}
                  matchType="all">
                  <PaymentType isAutopay />
                </ProtectedRoute>
              }
            />
            <Route path="payment-success" element={<InvoicePaymentSuccess />} />
            <Route path="pay-invoices" element={<InvoicesPaymentTable />} />
          </Route>
          <Route
            path="payments"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <PaymentHistoryLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="account-management"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <ProtectedRoute
                  cemFeature={CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT}
                  matchType="any">
                  <AccountManagement />
                </ProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoicing/account-management/:clientId"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <ProtectedRoute
                  cemFeature={CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT}
                  matchType="any">
                  <AccountManagement />
                </ProtectedRoute>
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="/questionnaires" element={<QuestionnaireBaseLayout />}>
          <Route
            index
            element={
              isQuestionnairesEnabled ? (
                <ProtectedRoute
                  cemFeature={CEM_FEATURE_QUESTIONNAIRE_ASSIGN}
                  cemFeatureFallback={
                    <Navigate to="/questionnaires/my" replace />
                  }
                  matchType="any">
                  <Navigate to="/questionnaires/queue" replace />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/questionnaires/my"
            element={
              isQuestionnairesEnabled ? (
                <ProtectedRoute
                  feature="Questionnaires"
                  cemFeature={[CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE]}
                  cemFeatureFallback={
                    <Navigate to="/questionnaires/queue" replace />
                  }
                  matchType="any">
                  <MyQuestionnaireLayout />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/questionnaires/queue"
            element={
              isQuestionnairesEnabled ? (
                <ProtectedRoute
                  feature="Questionnaires"
                  cemFeature={[CEM_FEATURE_QUESTIONNAIRE_ASSIGN]}
                  cemFeatureFallback={
                    <Navigate to="/questionnaires/my" replace />
                  }
                  matchType="any">
                  <SearchAndAssignLayout />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Route>
        <Route path="/projects/*" element={<ProjectsPage />} />
        <Route path="/notifications" element={<Notifications />} />
        {isUserProfileEnabled && (
          <Route path="/profile" element={<Profile />} />
        )}

        <Route
          path="/user-management"
          element={
            <ProtectedRoute
              feature="UserManagement"
              cemFeature={[CEM_FEATURE_CLIENT_ADMIN]}
              matchType="any">
              <UserManagement />
            </ProtectedRoute>
          }>
          <Route
            index
            element={
              <ProtectedRoute
                feature="UserManagement"
                cemFeature={[CEM_FEATURE_CLIENT_ADMIN]}
                matchType="any">
                <UserManagementLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="userprofile"
            element={
              <ProtectedRoute
                feature="UserManagement"
                cemFeature={[CEM_FEATURE_CLIENT_ADMIN]}
                matchType="any">
                <UserProfile />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/reportanissue"
          element={
            isSupportEnabled ? <ProtectedRouteError /> : <ReportAnIssue />
          }
        />
        <Route path="/faq" element={<Faq />} />
        <Route path="/debug/features" element={<FeaturesPage />} />
        <Route path="*" element={<NoMatch />} />
        <Route
          path="/support"
          element={
            isSupportEnabled ? (
              <Navigate to="/contact-us/support-page" replace />
            ) : (
              <Support />
            )
          }
        />
        <Route
          path="/contact-us"
          element={supportProtectedRoute(<ContactUsBaseLayout />)}>
          <Route
            index
            element={supportProtectedRoute(
              <Navigate to="/contact-us/support-page" replace />,
            )}
          />
          <Route
            path="support-page"
            element={supportProtectedRoute(<SupportPage />)}
          />
          <Route
            path="faq"
            element={
              isFAQRedesignEnabled
                ? supportProtectedRoute(<FaqRedesign />)
                : supportProtectedRoute(<Faq />)
            }
          />
          <Route
            path="feedback"
            element={supportProtectedRoute(<FeedbackForm />)}
          />
          {isAdminSettingsEnabled && (
            <Route
              path="admin-settings"
              element={
                <ProtectedRoute
                  feature="AdminSettings"
                  featureFallback={<Navigate to="/support" replace />}
                  cemFeature={[CEM_FEATURE_MANAGE_ADMIN_SETTINGS]}
                  cemFeatureFallback={<Navigate to="/support" replace />}
                  matchType="any">
                  <AdminSettingsForm />
                </ProtectedRoute>
              }
            />
          )}
        </Route>
        <Route
          path="/Report/:parentReportName/:childReportName"
          element={<ChildReportPage />}
        />
        <Route path="/Report/:reportName" element={<Report />} />
      </Route>
      <Route path="/m/Report/:reportName" element={<MobileReport />} />
    </Routes>
  )
}

export default AppRoutes
